import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import './CaptchaPopup.scss';

/** Props for the {@link CaptchaPopup} component. */
export interface CaptchaPopupProps {
    /** Whether the popup should be open. */
    isVisible: boolean;
    /** Full URL to the CAPTCHA stub endpoint (popup target). */
    stubUrl: string;
    /** Whether the stub page reported a WAF misconfiguration error. */
    captchaError: boolean;
    /** Called when the user closes the toast or the popup is closed. */
    onCancel: () => void;
    /** Called when the user clicks "Try again" after an error or timeout. */
    onRetry: () => void;
}

const TIMEOUT_MS = 180000;
const POPUP_FEATURES = 'width=500,height=600,scrollbars=yes,resizable=yes';

export function CaptchaPopup({ isVisible, stubUrl, captchaError, onCancel, onRetry }: CaptchaPopupProps) {
    const { t } = useTranslation();
    const [timedOut, setTimedOut] = useState(false);
    const [popupBlocked, setPopupBlocked] = useState(false);
    const popupRef = useRef<Window | null>(null);
    const timedOutRef = useRef(false);
    const captchaErrorRef = useRef(captchaError);
    captchaErrorRef.current = captchaError;

    useEffect(() => {
        if (!isVisible) {
            setTimedOut(false);
            setPopupBlocked(false);
            timedOutRef.current = false;
            if (popupRef.current && !popupRef.current.closed) {
                popupRef.current.close();
            }
            popupRef.current = null;
            return;
        }

        const popup = window.open(stubUrl, 'afd-captcha', POPUP_FEATURES);
        if (!popup || popup.closed) {
            setPopupBlocked(true);
            return;
        }
        popupRef.current = popup;

        const timeoutId = setTimeout(() => {
            // Only treat as a timeout if the popup is still open.
            if (!popupRef.current || popupRef.current.closed) {
                return;
            }

            timedOutRef.current = true;
            setTimedOut(true);
            popupRef.current.close();
        }, TIMEOUT_MS);

        const pollId = setInterval(() => {
            if (popupRef.current?.closed) {
                clearInterval(pollId);
                if (!timedOutRef.current && !captchaErrorRef.current) {
                    onCancel();
                }
            }
        }, 500);

        return () => {
            clearTimeout(timeoutId);
            clearInterval(pollId);
        };
    }, [isVisible, stubUrl, onCancel]);

    if (!isVisible) return null;

    const showError = timedOut || captchaError || popupBlocked;

    const handleRetry = () => {
        if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.close();
        }
        popupRef.current = null;
        setPopupBlocked(false);
        timedOutRef.current = false;
        onRetry();
    };

    const toast = (
        <div className="afd-captcha-toast" role="status" aria-live="polite">
            <div className="afd-captcha-toast__content">
                {showError ? (
                    <>
                        <span className="afd-captcha-toast__text">
                            {popupBlocked
                                ? t('AfdCaptcha.popupBlocked')
                                : t('AfdCaptcha.error')
                            }
                        </span>
                        <Button appearance="primary" size="small" onClick={handleRetry}>
                            {t('AfdCaptcha.retry')}
                        </Button>
                    </>
                ) : (
                    <span className="afd-captcha-toast__text">
                        {t('AfdCaptcha.waitingForPopup')}
                    </span>
                )}
                <Button appearance="subtle" size="small" icon={<Dismiss24Regular />} onClick={onCancel}
                    aria-label={t('AfdCaptcha.close')} data-testid="afd-captcha-close" />
            </div>
        </div>
    );

    return typeof document !== 'undefined' ? createPortal(toast, document.body) : toast;
}
